import {
  DefaultTransformer,
  deserialize,
  DocumentObject,
  JsonObject,
  transform,
  Transformer,
} from '@jsonapiworld/jsonapi-serializer';
import { backendApiGateway } from '~/client/backend';
import { CompanyResourceType } from '~/types/company';
import { FinancialStatementItemResourceType } from '~/types/financial-statement';
import {
  BookkeepingAccount,
  BookkeepingAccountFilters,
  BookkeepingAccountsResponse,
  BulkAssignFinancialStatementItemResponse,
  CreateBookkeepingAccount,
  UpdateBookkeepingAccount,
} from '~/types/general-bookkeeping';
import { MetaDataCursorPage } from '~/types/jsonapi';
import { isDefined } from '~/types/typeGuards';
import {
  buildJsonAPIQueryParams,
  jsonAPICursorPaginatorBuilder,
  jsonAPIFilterBuilder,
  jsonAPIIncludeBuilder,
  jsonAPISortBuilder,
} from '~/utils/jsonapi-query';

const BookkeepingAccountResourceType = 'bookkeeping-account';

const accountsPageSize = 200;

type BookkeepingAccountsIncludeOption = 'parent' | 'financialStatementItem';

interface FetchAccountsOptions {
  size?: number;
  cursor?: string;
  include?: BookkeepingAccountsIncludeOption[];
}

export const fetchBookkeepingAccounts = async (
  companyId: string,
  filters: BookkeepingAccountFilters = {},
  options?: FetchAccountsOptions,
): Promise<BookkeepingAccountsResponse> => {
  const filtersBuilder = jsonAPIFilterBuilder();
  filtersBuilder.eq('company.id', companyId);

  if (filters.search) filtersBuilder.ilike('search', filters.search);
  if (filters.code) {
    filtersBuilder.eq('code', filters.code);
  }
  if (filters.codes && filters.codes.length > 0) {
    filtersBuilder.in(
      'code',
      filters.codes.filter((code) => code !== null),
    );
  }
  if (filters.name) {
    filtersBuilder.eq('name', filters.name);
  }
  if (filters.type) {
    filtersBuilder.eq('accountType', filters.type);
  }
  if (filters.section) {
    filtersBuilder.eq('section', filters.section);
  }
  if (isDefined(filters.hasChildren)) {
    filtersBuilder.eq('hasChildren', filters.hasChildren);
  }
  if (isDefined(filters.used)) {
    filtersBuilder.eq('used', filters.used);
  }
  if (isDefined(filters.hasFinancialStatementItem)) {
    filtersBuilder.empty('financialStatementItem.id', !filters.hasFinancialStatementItem);
  }

  const sortBuilder = jsonAPISortBuilder([{ sort: 'code', direction: 'asc' }]);

  const paginationParamsBuilder = jsonAPICursorPaginatorBuilder();
  paginationParamsBuilder.size(options?.size ?? accountsPageSize);
  if (options?.cursor) paginationParamsBuilder.cursor(options.cursor);

  const includeBuilder = jsonAPIIncludeBuilder(options?.include || []);

  const { data: results } = await backendApiGateway.get<DocumentObject & { meta: { page: MetaDataCursorPage } }>(
    '/api/v1/bookkeeping-accounts',
    {
      params: buildJsonAPIQueryParams(filtersBuilder, paginationParamsBuilder, sortBuilder, includeBuilder),
    },
  );

  return {
    accounts: deserialize<BookkeepingAccount>(results) as BookkeepingAccount[],
    page: (results.meta as { page: MetaDataCursorPage }).page,
  };
};

export const fetchAllBookkeepingAccounts = async (
  companyId: string,
  filters?: BookkeepingAccountFilters,
  options?: { include?: BookkeepingAccountsIncludeOption[] },
): Promise<BookkeepingAccount[]> => {
  let allAccounts: BookkeepingAccount[] = [];
  let fetchedAccounts: BookkeepingAccount[] = [];
  let cursor: MetaDataCursorPage['cursor'] | null = null;

  do {
    const response = await fetchBookkeepingAccounts(companyId, filters ?? {}, {
      size: accountsPageSize,
      cursor: cursor || undefined,
      include: options?.include,
    });

    fetchedAccounts = response.accounts;
    allAccounts = allAccounts.concat(fetchedAccounts);
    cursor = response.page?.cursor || null;
  } while (fetchedAccounts.length === accountsPageSize);

  return allAccounts;
};

class CreateBookkeepingAccountTransformer extends Transformer<CreateBookkeepingAccount, {}> {
  constructor() {
    super();
    this.type = BookkeepingAccountResourceType;
    this.relationships = {
      company: ({ companyId }) =>
        transform()
          .withInput({ id: companyId })
          .withTransformer(new DefaultTransformer(CompanyResourceType))
          .toContext(),
      parent: ({ parentId }) =>
        transform()
          .withInput({ id: parentId })
          .withTransformer(new DefaultTransformer(BookkeepingAccountResourceType))
          .toContext(),
    };
  }

  transform(payload: CreateBookkeepingAccount): JsonObject {
    return {
      code: payload.code,
      name: payload.name,
    };
  }
}

export const createBookkeepingAccount = async (values: CreateBookkeepingAccount): Promise<BookkeepingAccount> => {
  const payload = transform<CreateBookkeepingAccount, {}>()
    .withInput(values)
    .withTransformer(new CreateBookkeepingAccountTransformer())
    .serialize();

  const { data: result } = await backendApiGateway.post<DocumentObject>('/api/v1/bookkeeping-accounts', payload);
  return deserialize<BookkeepingAccount>(result) as BookkeepingAccount;
};

class UpdateBookkeepingAccountTransformer extends Transformer<UpdateBookkeepingAccount, {}> {
  constructor(values: UpdateBookkeepingAccount) {
    super();
    this.type = BookkeepingAccountResourceType;

    if (values.financialStatementItemId !== undefined) {
      this.relationships = {
        financialStatementItem: ({ financialStatementItemId }) =>
          transform()
            .withInput(financialStatementItemId !== null ? { id: financialStatementItemId } : null)
            .withTransformer(new DefaultTransformer(FinancialStatementItemResourceType))
            .toContext(),
      };
    }
  }

  transform(payload: UpdateBookkeepingAccount): JsonObject {
    return {
      ...(payload.code !== undefined && { code: payload.code }),
      name: payload.name,
    };
  }
}

export const updateBookkeepingAccount = async (values: UpdateBookkeepingAccount): Promise<BookkeepingAccount> => {
  const payload = transform<UpdateBookkeepingAccount, {}>()
    .withInput(values)
    .withTransformer(new UpdateBookkeepingAccountTransformer(values))
    .serialize();

  const { data: result } = await backendApiGateway.patch<DocumentObject>(
    `/api/v1/bookkeeping-accounts/${values.id}`,
    payload,
  );
  return deserialize<BookkeepingAccount>(result) as BookkeepingAccount;
};

export const deleteBookkeepingAccount = async (id: BookkeepingAccount['id']): Promise<void> => {
  await backendApiGateway.delete(`/api/v1/bookkeeping-accounts/${id}`);
};

export const bulkAssignFinancialStatementItem = async (
  companyId: string,
  accountIds: string[],
  financialStatementItemId: string | null,
): Promise<BulkAssignFinancialStatementItemResponse> => {
  const { data: result } = await backendApiGateway.post<BulkAssignFinancialStatementItemResponse>(
    '/api/v1/bookkeeping-accounts/bulk-assign-financial-statement-item',
    { companyId, accountIds, financialStatementItemId },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
  return result;
};
